// src/directives/skeleton.js

// example usage:
// <div v-skeleton="{ show: loading }"></div>
// "loading" is a boolean that controls the visibility of the skeleton -> loading variable of the component
// works with any tag

const applySkeleton = (el, options) => {
    if (!options.show) {
        return;
    }

    if (el._skeletonActive) {
        return;
    }

    const computedStyle = window.getComputedStyle(el);
    const rect = el.getBoundingClientRect();
    
    if (!el._skeletonOriginal) {
        el._skeletonOriginal = {
            styles: {
                position: el.style.position,
                overflow: el.style.overflow,
                background: el.style.background,
                backgroundColor: el.style.backgroundColor,
                backgroundSize: el.style.backgroundSize,
                animation: el.style.animation,
                borderRadius: el.style.borderRadius,
                opacity: el.style.opacity,
                width: el.style.width,
                height: el.style.height,
                minWidth: el.style.minWidth,
                minHeight: el.style.minHeight
            },
            computedStyles: {
                width: rect.width || parseFloat(computedStyle.width) || el.offsetWidth,
                height: rect.height || parseFloat(computedStyle.height) || el.offsetHeight,
                borderRadius: computedStyle.borderRadius
            }
        };
    }

    const original = el._skeletonOriginal;
    const width = options.width || original.computedStyles.width || 100;
    const height = options.height || original.computedStyles.height || 20;
    const borderRadius = options.borderRadius || original.computedStyles.borderRadius || '4px';

    const currentPosition = computedStyle.position;
    if (currentPosition === 'static') {
        el.style.position = 'relative';
    }

    const finalWidth = options.width ? `${options.width}px` : (width > 0 ? `${width}px` : '100%');
    const finalHeight = options.height ? `${options.height}px` : (height > 0 ? `${height}px` : '100px');

    if (options.width || width > 0) {
        el.style.width = finalWidth;
        el.style.minWidth = finalWidth;
    }
    if (options.height || height > 0) {
        el.style.height = finalHeight;
        el.style.minHeight = finalHeight;
    }

    const skeletonOverlay = document.createElement('div');
    skeletonOverlay.className = 'vue-skeleton-overlay';
    skeletonOverlay.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        width: 100%;
        height: 100%;
        background: linear-gradient(90deg, ${options.backgroundColor} 25%, ${options.shimmerColor} 50%, ${options.backgroundColor} 75%);
        background-size: 200% 100%;
        animation: skeleton-shimmer ${options.animationDuration} infinite;
        border-radius: ${borderRadius};
        z-index: 10;
        pointer-events: none;
        display: block;
    `;

    el.style.opacity = '0.1';
    el.style.overflow = 'hidden';

    // Add overlay
    el.appendChild(skeletonOverlay);
    
    if (!document.getElementById('skeleton-keyframes')) {
        const style = document.createElement('style');
        style.id = 'skeleton-keyframes';
        style.textContent = `
            @keyframes skeleton-shimmer {
                0% { background-position: 200% 0; }
                100% { background-position: -200% 0; }
            }
        `;
        document.head.appendChild(style);
    }

    el.classList.add('vue-skeleton-active');
    el._skeletonActive = true;
};

const removeSkeleton = (el) => {
    if (!el._skeletonOriginal || !el._skeletonActive) return;

    const original = el._skeletonOriginal;

    const overlay = el.querySelector('.vue-skeleton-overlay');
    if (overlay) {
        overlay.remove();
    }

    const propsToClean = [
        { js: 'width', css: 'width' },
        { js: 'height', css: 'height' },
        { js: 'minWidth', css: 'min-width' },
        { js: 'minHeight', css: 'min-height' },
        { js: 'position', css: 'position' },
        { js: 'overflow', css: 'overflow' },
        { js: 'opacity', css: 'opacity' }
    ];

    propsToClean.forEach(prop => {
        if (!original.styles[prop.js] || original.styles[prop.js] === '') {
            el.style.removeProperty(prop.css);
        } else {
            el.style[prop.js] = original.styles[prop.js];
        }
    });

    Object.keys(original.styles).forEach(prop => {
        const alreadyHandled = propsToClean.some(p => p.js === prop);
        if (!alreadyHandled) {
            if (original.styles[prop]) {
                el.style[prop] = original.styles[prop];
            } else {
                el.style.removeProperty(prop);
            }
        }
    });

    el.classList.remove('vue-skeleton-active');
    el._skeletonActive = false;
};

export const vSkeleton = {
    mounted(el, binding) {
        const defaultOptions = {
            width: null,
            height: null,
            backgroundColor: 'hsl(240, 1%, 57%)',
            shimmerColor: 'hsl(0, 0%, 87%)',
            animationDuration: '1.5s',
            borderRadius: null,
            show: true
        };

        const options = binding.value ? { ...defaultOptions, ...binding.value } : defaultOptions;
        el._skeletonOptions = options;

        if (options.show) {
            requestAnimationFrame(() => {
                applySkeleton(el, options);
            });
        }
    },

    updated(el, binding) {
        const newOptions = binding.value ? { ...el._skeletonOptions, ...binding.value } : el._skeletonOptions;
        el._skeletonOptions = newOptions;

        if (newOptions.show && !el._skeletonActive) {
            applySkeleton(el, newOptions);
        } else if (!newOptions.show && el._skeletonActive) {
            removeSkeleton(el);
        }
    },

    unmounted(el) {
        removeSkeleton(el);
        delete el._skeletonOptions;
        delete el._skeletonOriginal;
        delete el._skeletonActive;
    }
};

// Register globally
export default {
    install(app) {
        app.directive('skeleton', vSkeleton);
    }
};